import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";

/* Shared by the dashboard (F-020) and the Trend-Dashboard (F-023). */

export interface SegmentedProps<T extends string> {
  testid: string;
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}

/**
 * Segmented control. Active segment wears the Ryddion-Orange accent (the
 * --primary token, #F89828) with dark text - UI accent only, never a series
 * color (the chart palette is disjoint from it).
 */
export function Segmented<T extends string>({
  testid,
  label,
  value,
  options,
  onChange,
}: SegmentedProps<T>): JSX.Element {
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium">{label}</div>
      <div
        role="group"
        aria-label={label}
        data-testid={testid}
        className="inline-flex flex-wrap rounded-md border bg-background p-0.5"
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={option.value === value}
            data-testid={`dashboard_segment_${option.value}`}
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded px-3 py-1 text-sm transition-colors",
              option.value === value
                ? "bg-primary font-medium text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ErrorBanner({
  section,
  onRetry,
}: {
  section: "tiles" | "trend" | "combos";
  onRetry: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-testid={`dashboard_error_${section}`}
      className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
    >
      <span>{t("errors.load")}</span>
      <Button
        data-testid={`dashboard_error-retry_${section}`}
        variant="outline"
        size="sm"
        onClick={onRetry}
      >
        {t("common.retry")}
      </Button>
    </div>
  );
}
